import { ChevronRight } from "lucide-react";

/**
 * Unified page header used across all dashboard pages.
 *
 * Usage:
 *   <PageHeader
 *     icon={<UtensilsCrossed className="w-5 h-5" />}
 *     title="Recetas"
 *     description="Genera, edita y gestiona las recetas de tus platos."
 *     breadcrumb={[{ label: "Carta y cocina" }, { label: "Recetas" }]}
 *     actions={<Button>Nueva receta</Button>}
 *   />
 *
 * Designed to be:
 *  - Dark/Light aware (uses semantic tokens only)
 *  - Compact on mobile, generous on desktop
 *  - Sticky-friendly when wrapped in a sticky container
 */
export function PageHeader({
  icon,
  title,
  description,
  breadcrumb = [],
  actions,
  className = "",
  testId,
}) {
  return (
    <header
      className={`mb-6 lg:mb-8 ${className}`}
      data-testid={testId || "page-header"}
    >
      {breadcrumb.length > 0 && (
        <nav
          className="flex items-center gap-1 text-xs text-muted-foreground mb-2"
          aria-label="Breadcrumb"
          data-testid="page-header-breadcrumb"
        >
          {breadcrumb.map((b, idx) => {
            const isLast = idx === breadcrumb.length - 1;
            return (
              <span key={`${b.label}-${idx}`} className="inline-flex items-center gap-1">
                {b.href ? (
                  <a
                    href={b.href}
                    className="hover:text-foreground transition-colors"
                  >
                    {b.label}
                  </a>
                ) : (
                  <span className={isLast ? "text-foreground font-medium" : ""}>
                    {b.label}
                  </span>
                )}
                {!isLast && <ChevronRight className="w-3 h-3 text-muted-foreground/60" />}
              </span>
            );
          })}
        </nav>
      )}

      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-start gap-3">
            {icon && (
              <span
                className="flex-shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-xl bg-primary/10 text-primary mt-0.5"
                data-testid="page-header-icon"
              >
                {icon}
              </span>
            )}
            <div className="min-w-0 flex-1">
              <h1
                className="font-heading text-2xl lg:text-3xl font-semibold tracking-tight text-foreground"
                data-testid="page-header-title"
              >
                {title}
              </h1>
              {description && (
                <p
                  className="mt-1 text-sm text-muted-foreground max-w-2xl"
                  data-testid="page-header-description"
                >
                  {description}
                </p>
              )}
            </div>
          </div>
        </div>

        {actions && (
          <div
            className="flex items-center gap-2 flex-wrap lg:flex-nowrap"
            data-testid="page-header-actions"
          >
            {actions}
          </div>
        )}
      </div>
    </header>
  );
}

export default PageHeader;
